import { Modal, Progress, Select, Typography } from "antd";
import PrimaryButton from "../Forms/PrimaryButton";
import { useNavigate } from "react-router-dom";
import { formatDate, handleRouteNav, numberFormatter } from "../../utils";
import { useSelector } from "react-redux";
import { useApiCall } from "../../services/api";
import { useEffect, useMemo, useState } from "react";

const { Title } = Typography;

export default function NewOfferModal({ open, onClose, loading, schemeData: offerSchemeData }) {
  const navigate = useNavigate();
  const { postApi, loading: apiLoading } = useApiCall();
  const { company_name, role_id, customer_id, is_agent } = useSelector((state) => state.auth);

  const [selectedSchemeData, setSelectedSchemeData] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [allCustomers, setAllCustomers] = useState([]);

  const schemeData = selectedSchemeData || offerSchemeData || {};

  const offerCustomersIds = useMemo(() => {
    try {
      return JSON.parse(offerSchemeData?.customer_ids || "[]");
    } catch {
      return [];
    }
  }, [offerSchemeData?.customer_ids]);

  const isAgentOffer = offerCustomersIds.includes(customer_id);

  const disclaimers = useMemo(() => {
    const baseDisclaimers = [
      "Meeting the quantity threshold alone does not guarantee that the scheme will apply.",
      "The scheme may not apply to all products and orders, and there may be other criteria for it to apply.",
    ];

    if (schemeData?.remarks?.trim()) {
      baseDisclaimers.push(
        "Some of the additional criteria for this scheme are mentioned as Special Remarks. Please click on ‘View Details’ below to know more.",
      );
    }

    baseDisclaimers.push("For any clarifications or further information regarding this scheme, please contact your sales representative.");

    return baseDisclaimers;
  }, [schemeData?.remarks]);

  const { currentVariantSummary, title, subtitle } = useMemo(() => {
    const variants = schemeData?.variant_summary || [];
    const sorted = [...variants].sort((a, b) => a?.required_quantity - b?.required_quantity).map((a, i) => ({ ...a, index: i + 1 }));

    let current = sorted.find((item) => item?.status === "active");

    if (!current && sorted.length > 0) {
      const allCompleted = sorted.every((item) => item?.status === "completed");
      current = allCompleted ? sorted[sorted.length - 1] : sorted[0];
    }

    let modalTitle = "";
    let modalSubtitle = "";

    if (is_agent && customer_id != selectedCustomerId) {
      switch (current?.status) {
        case "active":
          modalTitle = "Client’s Scheme Is Still Active";
          modalSubtitle =
            "Your client’s scheme is still active, and available.\nThere are benefits for them, still waiting to be unlocked.\nDon’t miss out!";
          break;
        case "completed":
          modalTitle = "Client’s Target Has Been Hit!";
          modalSubtitle =
            "Congratulations! Your client has successfully achieved their target, for this scheme. If eligible, they may Claim Their Benefits!";
          break;
        case "inactive":
          modalTitle = "New Scheme For Client";
          modalSubtitle = "Your client has a new untapped scheme. To tap into this scheme and its benefits, get started, and Place Orders today!";
          break;
        default:
          break;
      }
    } else {
      switch (current?.status) {
        case "active":
          modalTitle = "Your Scheme Is Still Active";
          modalSubtitle = "Your scheme is still active, and available.\nThere are benefits for you, still waiting to be unlocked.\nDon’t miss out!";
          break;
        case "completed":
          modalTitle = "Your Target Has Been Hit!";
          modalSubtitle = "Congratulations! You have successfully achieved your target, for this scheme. If eligible, you may Claim Your Benefits!";
          break;
        case "inactive":
          modalTitle = "New Scheme For You";
          modalSubtitle = "You have a new untapped scheme. To tap into this scheme and its benefits, get started, and Place Orders today!";
          break;
        default:
          break;
      }
    }

    return { currentVariantSummary: current, title: modalTitle, subtitle: modalSubtitle };
  }, [schemeData?.variant_summary]);

  const fetchOfferDetails = async (selected_customer_id, scheme_id) => {
    if (!scheme_id) return null;
    const payload = { customer_id: selected_customer_id, offer_id: scheme_id };
    const res = await postApi("offer/get-detail", payload);
    return res?.data || null;
  };

  const handleCustomerSelect = async (customerId) => {
    setSelectedCustomerId(customerId);
    const response = await fetchOfferDetails(customerId, offerSchemeData?.offer_id);
    if (response) {
      setSelectedSchemeData(response);
    }
  };

  useEffect(() => {
    const initAgentData = async () => {
      const endpoint = isAgentOffer ? "sales/agent/agent-customer-list" : "sales/assign-customer-list";
      const payload = isAgentOffer ? { agent_customer_id: customer_id } : { agent_id: customer_id };

      const res = await postApi(endpoint, payload);
      const customers = res?.data || [];

      const filteredCustomers = customers.filter((customer) => offerCustomersIds.includes(customer.customer_id));
      setAllCustomers(filteredCustomers);

      const targetCustomerId = !isAgentOffer && filteredCustomers.length > 0 ? filteredCustomers[0].customer_id : customer_id;

      setSelectedCustomerId(targetCustomerId);

      const offerData = await fetchOfferDetails(targetCustomerId, offerSchemeData?.offer_id);
      if (offerData) {
        setSelectedSchemeData(offerData);
      }
    };

    if (open && is_agent) {
      initAgentData();
    }
  }, [open, is_agent, customer_id, isAgentOffer, offerCustomersIds, offerSchemeData?.offer_id]);

  useEffect(() => {
    if (!open) {
      setSelectedSchemeData(null);
      setSelectedCustomerId(null);
      setAllCustomers([]);
    }
  }, [open]);

  return (
    <Modal className="form-css custom-modal" open={open} footer={null} onCancel={onClose} width={500}>
      <Title level={2} className="font-abhaya mb-2">
        {title}
      </Title>
      <b>Hi {company_name},</b>
      <p className="whitespace-pre-line">{subtitle}</p>

      {is_agent === 1 && (
        <div className="mt-10 gap-4 items-center w-full">
          <div className="text-left whitespace-nowrap mb-2">
            <b>Client</b>
          </div>
          <Select
            placeholder="Select Client"
            className="w-[260px]"
            onChange={handleCustomerSelect}
            value={selectedCustomerId}
            loading={apiLoading}
            showSearch
            filterOption={(input, option) =>
              String(option?.children || "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {allCustomers.map((customer) => (
              <Select.Option key={customer.customer_id} value={customer.customer_id}>
                {customer.company_name}
              </Select.Option>
            ))}
          </Select>
        </div>
      )}

      <div className={`flex gap-4 items-center ${is_agent === 1 ? "mt-6" : "mt-10"}`}>
        <div className="bg-[#F4F4F4] p-2 w-fit" style={{ borderRadius: "24px" }}>
          <img className="min-h-[64px] min-w-[64px]" src="/assets/icons/welcome-logo.png" alt="welcome" />
        </div>

        <div className="text-left">
          <strong className="text-xl text-[#000]">{schemeData?.title}</strong>
          <div className="text-[#00000080] mt-2">
            {formatDate(schemeData?.from_date)} - {formatDate(schemeData?.to_date)}
          </div>
        </div>
      </div>

      {currentVariantSummary?.status !== "inactive" && (
        <div className="text-left mt-6">
          <div className="text-[#000] mb-2">
            <b>{numberFormatter(currentVariantSummary?.achieved_quantity)}</b> out of{" "}
            <b>{numberFormatter(currentVariantSummary?.required_quantity)}</b> {currentVariantSummary?.quantity_type === "Cases" ? "cases" : "meters"}
          </div>

          <Progress
            percent={currentVariantSummary?.progress_percent}
            showInfo={false}
            strokeWidth={16}
            strokeColor="#a33434"
            trailColor="#A3343426"
            className={currentVariantSummary?.progress_percent >= 100 ? "glow-progress-full" : ""}
          />
        </div>
      )}

      <div className="text-left mt-4">
        <strong className="text-xl text-[#000]">Slab {currentVariantSummary?.index}</strong>

        <div className="flex flex-col mt-4 gap-3">
          <div className="flex gap-3 items-center">
            <img src="/assets/icons/quantity.svg" alt="quantity" />
            <div className="">
              <div className="text-[#00000080]">Quantity</div>
              <strong className="text-lg text-[#000] mt-2">
                {numberFormatter(currentVariantSummary?.required_quantity)} {currentVariantSummary?.quantity_type === "Cases" ? "Cases" : "Mtrs"}
              </strong>
            </div>
          </div>

          <div className="flex gap-3 items-center">
            <img src="/assets/icons/incentive.svg" alt="incentive" />
            <div className="">
              <div className="text-[#00000080]">Incentive</div>
              <strong className="text-lg text-[#000] mt-2">{currentVariantSummary?.credit_value}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-md font-bold text-[#000]">Disclaimers</h2>
        </div>

        <div className="space-y-4">
          {disclaimers.map((item, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1.5">
                <div className="w-2 h-2 bg-[#A33434] rounded-full"></div>
              </div>
              <p className="flex-1 text-left text-gray-600 leading-relaxed text-sm">{item}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center mt-6">
        <PrimaryButton
          text={"View Details"}
          type="submit"
          loading={loading}
          className="me-[30px]"
          onClick={() =>
            navigate(handleRouteNav(`/home/scheme-details`, role_id), {
              state: { offerId: schemeData?.offer_id, selected_customer_id: selectedCustomerId },
            })
          }
        />
      </div>
    </Modal>
  );
}
